import React from "react";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { postFetcher } from "api/swr";
import Loader from "@/components/ui/Loader";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";

const ChangeCover = () => {
  const { id } = useParams();
  const { toast } = useToast();

  const { data: frame, isLoading: frameLoading } = useSWR(
    id ? [`/frames/${id}`] : null,
  );
  const { data: candidate, isLoading: rankingLoading } = useSWR(
    id ? [`/frames/${id}/avatars/reCreate-ranking`] : null,
  );

  const { trigger, isMutating } = useSWRMutation(
    id ? `/frames/${id}/cover/apply` : null,
    postFetcher,
  );

  const previewUrl = frame?.preview || null;
  const candidateUrl = candidate?.url || null;
  const canReplace = Boolean(candidateUrl);

  const handleApply = async () => {
    if (!id || !canReplace) {
      toast({
        title: "无法替换",
        description: "没有可用的候选图。",
        variant: "destructive",
      });
      return;
    }
    try {
      const res = await trigger({ sourceAvatarId: candidate.id });
      if (res?.dryRun) {
        toast({
          title: "DRY-RUN（未入队）",
          description: `newAvatarPreviewId: ${res.newAvatarPreviewId ?? ""}`,
        });
      } else {
        toast({
          title: "已提交",
          description: "已发送更新模版封面任务。",
          className: "bg-green-500 text-white",
        });
      }
    } catch (e) {
      toast({
        title: "提交失败",
        description: e?.message || String(e),
        variant: "destructive",
      });
    }
  };

  const loading = frameLoading || rankingLoading;

  return (
    <div className="mx-auto flex w-full max-w-lg flex-col gap-3 px-3 pb-6 pt-3">
      <h1 className="text-lg font-semibold leading-tight">更换封面：{id}</h1>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader />
        </div>
      ) : (
        <div className="flex flex-col gap-10 mt-16">
          <div
            className="grid w-full grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-x-2 gap-y-1.5"
            aria-label="当前封面与新封面对比"
          >
            <div className="min-w-0">
              {previewUrl ? (
                <>
                  <span className="text-muted-foreground text-xs">
                    当前封面
                  </span>

                  <img
                    src={previewUrl}
                    alt=""
                    className="max-h-[min(40vh,280px)] w-full object-contain"
                  />
                </>
              ) : null}
            </div>
            <ArrowRight
              className="text-muted-foreground h-7 w-7 shrink-0 justify-self-center sm:h-8 sm:w-8"
              strokeWidth={2}
              aria-hidden
            />
            <div className="min-w-0">
              {candidateUrl ? (
                <>
                  <span className="text-muted-foreground text-xs">
                    新封面(更换后没有水印)
                  </span>
                  <img
                    src={candidateUrl}
                    alt=""
                    className="max-h-[min(40vh,280px)] w-full object-contain"
                  />
                </>
              ) : null}
            </div>
          </div>

          <Button
            type="button"
            className="h-11 w-full mt-12"
            disabled={!canReplace || isMutating}
            onClick={handleApply}
          >
            {isMutating ? "提交中…" : "替换"}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ChangeCover;
